export const NOTIFICATION_COMMAND = "send:notification";
export const NOTIFICATION_EMAIL_TYPE = "email";
export const NOTIFICATION_IN_APP_TYPE = "in_app";
export const NOTIFICATION_SUBJECT_FIELD = "subject";
export const NOTIFICATION_MESSAGE_FIELD = "message_template";
export const NOTIFICATION_PROFILES_FIELD = "profiles";
export const NOTIFICATION_RECEIVER_FIELD = "receiver";

export function isNotificationCommand(nodeInfo) {
  const command = nodeInfo?.cmd ?? nodeInfo?.command;
  return command === NOTIFICATION_COMMAND;
}

export function withNotificationProfilesRequirement(requirements, nodeInfo) {
  const list = Array.isArray(requirements) ? requirements : [];

  if (
    !isNotificationCommand(nodeInfo)
    || list.some((requirement) => requirement?.type === NOTIFICATION_PROFILES_FIELD)
  ) {
    return list;
  }

  return [...list, { type: NOTIFICATION_PROFILES_FIELD, multiple: 1 }];
}

export function orderNotificationRequirements(requirements, nodeInfo) {
  if (!isNotificationCommand(nodeInfo)) return requirements;

  const profilesIndex = requirements.findIndex((requirement) =>
    requirement?.type === NOTIFICATION_PROFILES_FIELD
  );
  if (profilesIndex === -1) {
    return requirements;
  }

  const orderedTypes = [
    NOTIFICATION_PROFILES_FIELD,
    NOTIFICATION_SUBJECT_FIELD,
    NOTIFICATION_MESSAGE_FIELD,
  ];
  const firstOrderedIndex = Math.min(...orderedTypes
    .map((type) => requirements.findIndex((requirement) => requirement?.type === type))
    .filter((index) => index >= 0));
  const orderedRequirements = requirements.filter(
    (requirement) => !orderedTypes.includes(requirement?.type)
  );
  const orderedNotificationRequirements = orderedTypes
    .map((type) => requirements.find((requirement) => requirement?.type === type))
    .filter(Boolean);

  orderedRequirements.splice(firstOrderedIndex, 0, ...orderedNotificationRequirements);

  return orderedRequirements;
}

export function isNotificationRequirementVisible(field, nodeInfo, storage) {
  if (!isNotificationCommand(nodeInfo)) return true;

  if (field?.type === NOTIFICATION_SUBJECT_FIELD) {
    return [NOTIFICATION_EMAIL_TYPE, NOTIFICATION_IN_APP_TYPE].includes(storage?.type);
  }

  if (field?.type === NOTIFICATION_PROFILES_FIELD) {
    return storage?.type === NOTIFICATION_IN_APP_TYPE;
  }

  if (field?.type === NOTIFICATION_RECEIVER_FIELD) {
    return storage?.type !== NOTIFICATION_IN_APP_TYPE;
  }

  return true;
}

export function usesMultilineNotificationTemplate(field, nodeInfo) {
  return isNotificationCommand(nodeInfo)
    && field?.type === NOTIFICATION_MESSAGE_FIELD;
}

export function isOptionalNotificationRequirement(nodeData, requirement) {
  return !isNotificationRequirementVisible(requirement, nodeData, nodeData?.storage);
}

export function mergeNotificationSubjectRequirement(nodeData, commandOptions = []) {
  if (nodeData?.cmd !== NOTIFICATION_COMMAND) return nodeData;

  const requirements = Array.isArray(nodeData.requirements)
    ? nodeData.requirements
    : [];
  const command = commandOptions.find(
    (option) => option.value === nodeData.value || option.cmd === nodeData.cmd
  );
  const notificationRequirements = [
    NOTIFICATION_SUBJECT_FIELD,
    NOTIFICATION_PROFILES_FIELD,
  ];
  const commandRequirements = command?.requirements || [];
  const fallbackProfilesRequirement = { type: NOTIFICATION_PROFILES_FIELD, multiple: 1 };
  const availableRequirements = commandRequirements.some(
    (requirement) => requirement.type === NOTIFICATION_PROFILES_FIELD
  )
    ? commandRequirements
    : [...commandRequirements, fallbackProfilesRequirement];
  const missingRequirements = availableRequirements.filter(
    (requirement) => notificationRequirements.includes(requirement.type)
      && !requirements.some((current) => current.type === requirement.type)
  );
  if (!missingRequirements.length) return nodeData;

  return {
    ...nodeData,
    requirements: [...requirements, ...missingRequirements.map((requirement) => ({ ...requirement }))],
  };
}
